// Focused mode for operator apps (REDISENO_UI.md 3.34, step C5 of the
// redesign). While an app is open in view mode under the new interface, the
// app takes the whole window: the sidebar folds to icons, the top bar hides
// and the app keeps one "Salir de la app" button. Nothing here is persisted:
// the user's own sidebar preference (uiStore.navCollapsed) is never touched,
// so leaving the app restores the sidebar exactly as it was.
//
// Operators also land on their app list: a user whose profile grants Apps and
// nothing to connect, model, build, analyze or administer opens maic there
// instead of on Home.
import { create } from 'zustand';
import { useEffect, useRef } from 'react';
import { DEFAULT_PAGE, DEFAULT_PAGE_V2, ROUTES, type NavGroupId } from './routes';
import { isGateableModule, moduleEnabled, type EffectivePermissions } from '../lib/permissionCatalog';
import { usePermissionStore } from '../store/permissionStore';
import { useNavigationStore } from '../store/navigationStore';

interface AppFocusState {
  /** An app is open in focused mode. */
  focused: boolean;
  /** The folded sidebar was opened by hand while focused (until the app closes). */
  railOpen: boolean;
  enter: () => void;
  exit: () => void;
  toggleRail: () => void;
}

export const useAppFocusStore = create<AppFocusState>((set) => ({
  focused: false,
  railOpen: false,
  enter: () => set({ focused: true, railOpen: false }),
  exit: () => set({ focused: false, railOpen: false }),
  toggleRail: () => set((s) => ({ railOpen: !s.railOpen })),
}));

/** Puts the shell in focused mode while the calling component is mounted. */
export function useAppFocus(active: boolean) {
  const enter = useAppFocusStore((s) => s.enter);
  const exit = useAppFocusStore((s) => s.exit);
  useEffect(() => {
    if (!active) return;
    enter();
    return exit;
  }, [active, enter, exit]);
}

// Groups whose pages make someone a builder or an analyst rather than an
// operator. Workspace pages (Home, Files, Inbox) and Operate do not count.
const NON_OPERATOR_GROUPS: NavGroupId[] = ['connect', 'model', 'build', 'analyze', 'admin'];

const moduleOf = (r: { page: string; permissionModule?: string }) => r.permissionModule ?? r.page;
// Modules an operator page is gated by. Documents and solution designs sit
// under Analyze but are gated by Dashboards ('apps'), which stays operator.
const OPERATOR_MODULES = new Set(ROUTES.filter((r) => !NON_OPERATOR_GROUPS.includes(r.group)).map(moduleOf));

/** Gateable modules behind the non-operator groups, once each. */
export const NON_OPERATOR_MODULES: string[] = [...new Set(
  ROUTES
    .filter((r) => NON_OPERATOR_GROUPS.includes(r.group))
    .map(moduleOf)
    .filter((m) => isGateableModule(m) && !OPERATOR_MODULES.has(m)),
)];

/** True for a profile that only operates: Apps granted, and no module of
 *  Connect, Model, Build, Analyze or Admin. A full (unconstrained) profile is
 *  never an operator; neither is one still loading. */
export function isOperatorOnly(perms: EffectivePermissions | null): boolean {
  if (!perms || perms.full) return false;
  if (!moduleEnabled(perms, 'apps-app')) return false;
  return !NON_OPERATOR_MODULES.some((m) => moduleEnabled(perms, m));
}

const isRootPath = () =>
  typeof window !== 'undefined' && (window.location.pathname === '/' || window.location.pathname === '/index.html');

/** Under the new interface, a visit to the bare root by an operator-only
 *  user lands on the app list instead of Home. Decided once, when the
 *  permission document first answers; a deep link or a page the user already
 *  moved to is left alone. Called by App.tsx's AuthGate. */
export function useOperatorLanding(isAuthenticated: boolean, uiV2: boolean) {
  const effective = usePermissionStore((s) => s.effective);
  // Captured at the first render: AuthGate's own redirect to Home moves the
  // URL off '/' before the permission document arrives.
  const fromRoot = useRef(isRootPath());
  useEffect(() => {
    if (!isAuthenticated || !uiV2 || !effective || !fromRoot.current) return;
    fromRoot.current = false;
    if (!isOperatorOnly(effective)) return;
    const nav = useNavigationStore.getState();
    const onLanding = nav.currentPage === DEFAULT_PAGE || nav.currentPage === DEFAULT_PAGE_V2;
    if (onLanding && !nav.pendingAppId) nav.redirectTo('apps-app');
  }, [isAuthenticated, uiV2, effective]);
}
